import React, { useEffect, useRef, useState } from 'react';
import ApiClient from '../../utils/ApiClient';
import { runAnalyzeJob, findActiveJob, resolveFinishedJob, waitForJob, jobProgressLabel, SCOPE_DRAFT } from '../../utils/plannerJobs';
import { boxReady, spreadWatchRequest } from '../../utils/spreadImport';

const btn = 'px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed';

const packaging = (pkg) => ({
  unitsPerBox: pkg.unitsPerBox, boxLengthIn: pkg.boxLengthIn, boxWidthIn: pkg.boxWidthIn,
  boxHeightIn: pkg.boxHeightIn, boxWeightLbs: pkg.boxWeightLbs,
});
// Like V1: only a human-vouched carton counts as confirmed; absent means false.
const drawItem = (line, kind, qty) => ({
  asin: line.asin, qty, ...packaging(line.pkg), cartonConfirmed: !!line.pkg?.packagingConfirmed,
});

/** Draw now / Keep drawing for a line's depth and spread parts. Never confirms anything. */
export default function LineActions({ line, onDrawn }) {
  const [state, setState] = useState({}); // kind -> { text, error }
  const alive = useRef(true);
  const scopeKeyOf = (kind) => `v2-${line.asin}-${kind}`;
  const ready = boxReady(line.pkg);
  const why = ready ? '' : 'Set box size first';
  const setKind = (kind, v) => setState((s) => ({ ...s, [kind]: v }));

  useEffect(() => {
    alive.current = true;
    ['depth', 'spread'].forEach(async (kind) => {
      try {
        const job = await findActiveJob(SCOPE_DRAFT, `v2-${line.asin}-${kind}`);
        if (!job || !alive.current) return;
        setKind(kind, { text: 'Drawing…' });
        const finished = await waitForJob(job.jobId, {
          onProgress: (j) => { if (alive.current) setKind(kind, { text: jobProgressLabel(j, { singleStepLabel: 'Drawing…' }) }); },
          isCancelled: () => !alive.current,
        });
        if (!finished || !alive.current) return;
        await resolveFinishedJob(finished);
        if (!alive.current) return;
        setKind(kind, { text: 'Drawn' });
        if (onDrawn) onDrawn(line.asin);
      } catch (err) {
        if (alive.current) setKind(kind, { error: err.message || 'Draw failed' });
      }
    });
    return () => { alive.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line.asin]);

  const qtyOf = (kind) => (kind === 'depth' ? line.depthQty : line.spreadQty);

  const drawNow = async (kind) => {
    setKind(kind, { text: 'Drawing…' });
    try {
      await runAnalyzeJob({
        url: '/api/shipment-planner/analyze-asin-list/async',
        body: { items: [drawItem(line, kind, qtyOf(kind))] },
        scopeType: SCOPE_DRAFT,
        scopeKey: scopeKeyOf(kind),
        label: `V2 ${kind} ${line.asin}`,
        onProgress: (job) => { if (alive.current) setKind(kind, { text: jobProgressLabel(job, { singleStepLabel: 'Drawing…' }) }); },
        isCancelled: () => !alive.current,
      });
      if (!alive.current) return;
      setKind(kind, { text: 'Drawn' });
      if (onDrawn) onDrawn(line.asin);
    } catch (err) {
      if (alive.current) setKind(kind, { error: err.response?.data?.error || err.message || 'Draw failed' });
    }
  };

  const keepDrawing = async (kind) => {
    setKind(kind, { text: 'Starting…' });
    try {
      let body;
      if (kind === 'depth') {
        body = { items: [drawItem(line, kind, line.depthQty)], source: line.source, kickoff: true };
      } else {
        body = spreadWatchRequest(line, line.spreadQty, line.pkg.unitsPerBox, `${line.source} spread`);
        // F8: same item shape as Draw now, cartonConfirmed included.
        body.items = body.items.map((it) => ({ ...it, ...drawItem(line, kind, it.qty) }));
      }
      await ApiClient.post('/api/shipment-planner/watch', body);
      setKind(kind, { text: 'Watching every 12 h' });
    } catch (err) {
      setKind(kind, { error: err.response?.data?.error || err.message || 'Could not start the watch' });
    }
  };

  const group = (kind, label) => {
    if (!(qtyOf(kind) > 0)) return null;
    const st = state[kind] || {};
    const busy = st.text === 'Drawing…' || st.text === 'Starting…';
    return (
      <div className="flex flex-wrap items-center gap-1 mb-1" data-testid={`actions-${kind}-${line.asin}`}>
        <span className="text-xs text-gray-500 dark:text-gray-400 w-12">{label}</span>
        <button type="button" className={btn} disabled={!ready || busy} title={why} onClick={() => drawNow(kind)}>Draw now</button>
        <button type="button" className={btn} disabled={!ready || busy || (kind === 'spread' && !line.spreadLive?.missingByRegion)}
          title={why} onClick={() => keepDrawing(kind)}>Keep drawing</button>
        {st.text && <span className="text-xs text-gray-600 dark:text-gray-300">{st.text}</span>}
        {st.error && <span className="text-xs text-red-600 dark:text-red-400">{st.error}</span>}
      </div>
    );
  };

  return <div>{group('depth', 'Depth')}{group('spread', 'Spread')}</div>;
}
